import {createAsyncThunk} from '@reduxjs/toolkit';
import {Nodes} from 'common/src/modules/nodes/nodes.types';
import {
  setNodes,
  setNodesByZIndexOrder,
  setNodesWithDisabledHideOutputButton,
} from '../campaignLogic.slice';
import createDisconnectedMapNodesList from '../utils/createDisconnectedMapNodesList';
import {sharedCampaigns} from '@cl/shared';
import {BreadcrumbName} from 'common/src/modules/breadcrumbs/breadcrumbs.enums';
import {CampaignTreeType} from 'common/src/modules/campaigns/campaigns.types';
import {CAMPAIGN_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import _map from 'lodash/map';
import _uniq from 'lodash/uniq';
import {RootState} from 'common/src/redux/store';
import getNodesWithDisabledOutputButton from 'common/src/components/CLCardsMap/CardsMapContainer/utils/collapseNodes/getNodesWithDisabledOutputButton';

/**
 * Updates the campaign nodes in the redux state.
 * @param props - The properties of the campaign nodes to update.
 * @param dispatch - The dispatch function.
 * @returns The response from the API.
 * @description This thunk updates the campaign nodes in the redux state.
 * It converts the connected nodes to disconnected nodes (break circular references),
 * and updates the redux state with the disconnected nodes.
 */
export const updateCampaignNodes = createAsyncThunk(
  `${BreadcrumbName.CampaignLogic}/updateCampaignNodes`,
  async (
    props: {connectedNodes: Nodes; campaignTreeType: CampaignTreeType},
    {dispatch, getState},
  ) => {
    const state = getState() as RootState;
    const {connectedNodes, campaignTreeType} = props;
    const hiddenNodeConnectors = state.campaignPage.campaignLogic.hiddenNodeConnectors;
    sharedCampaigns.nodesRef[campaignTreeType] = connectedNodes;

    // Convert connected nodes to disconnected nodes (break circular references)
    const nodesList = Object.values(connectedNodes);
    const disconnectedNodes = createDisconnectedMapNodesList(nodesList);
    const nodesOrder = _uniq([..._map(nodesList, 'id'), CAMPAIGN_NODE_ID]);

    // Update redux with disconnected nodes
    dispatch(setNodes({nodes: disconnectedNodes, type: campaignTreeType}));

    dispatch(
      setNodesByZIndexOrder({
        order: nodesOrder,
        type: campaignTreeType,
      }),
    );

    dispatch(
      setNodesWithDisabledHideOutputButton(
        getNodesWithDisabledOutputButton(nodesOrder, connectedNodes, hiddenNodeConnectors || []),
      ),
    );
  },
);
